import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Alert, Badge, Button, Card, Col, Form, InputGroup, ListGroup, Modal, Row, Spinner } from 'react-bootstrap';
import { api } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { FLEXIVEIS, PAGAMENTOS, STATUS, TIPOS_IMOVEL, dataHora, dinheiro } from './rotulos';

const VAZIO = {
  nome: '', telefone: '', email: '', whatsapp: true, finalidade: 'venda', tipoImovel: '', valorMin: '', valorMax: '', valorAproximado: false,
  dormitorios: '', suites: '', garagens: '', banheiros: '', areaMin: '', bairros: '', cidades: '', condominios: '', emCondominio: '',
  pagamento: '', oferecePermuta: false, flexiveis: [], observacoes: '', textoOriginal: '', status: 'aberto', alertar: true,
};

const texto = (v) => (v === null || v === undefined ? '' : String(v));

// pedido da API → valores dos campos da tela (tudo texto, listas separadas por vírgula)
function deApi(p) {
  return {
    ...VAZIO,
    ...Object.fromEntries(['nome', 'telefone', 'email', 'finalidade', 'tipoImovel', 'valorMin', 'valorMax', 'dormitorios', 'suites', 'garagens', 'banheiros', 'areaMin', 'pagamento', 'observacoes', 'textoOriginal', 'status'].map((k) => [k, texto(p[k])])),
    finalidade: p.finalidade || 'venda',
    status: p.status || 'aberto',
    valorAproximado: p.valorAproximado === true,
    oferecePermuta: p.oferecePermuta === true,
    alertar: p.alertar !== false,
    bairros: (p.bairros || []).join(', '),
    cidades: (p.cidades || []).join(', '),
    condominios: (p.condominios || []).join(', '),
    emCondominio: p.emCondominio === true ? 'true' : p.emCondominio === false ? 'false' : '',
    flexiveis: p.flexiveis || [],
  };
}

// valores da tela → o que a API espera (número vazio vira null)
function paraApi(v) {
  const numero = (x) => (x === '' ? null : Number(x));
  return {
    ...v,
    valorMin: numero(v.valorMin), valorMax: numero(v.valorMax), dormitorios: numero(v.dormitorios), suites: numero(v.suites),
    garagens: numero(v.garagens), banheiros: numero(v.banheiros), areaMin: numero(v.areaMin),
    emCondominio: v.emCondominio === '' ? null : v.emCondominio === 'true',
    pagamento: v.pagamento || null,
    tipoImovel: v.tipoImovel || null,
  };
}

// O que a imobiliária TEM para este pedido (catálogo da empresa toda).
function ImoveisDoPedido({ publicId, versao }) {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState(false);
  const [carregando, setCarregando] = useState(true);

  const carregar = useCallback(() => {
    setCarregando(true);
    setErro(false);
    api
      .get(`/pedidos/${publicId}/imoveis`)
      .then((r) => setDados(r.data))
      .catch(() => setErro(true))
      .finally(() => setCarregando(false));
  }, [publicId]);

  useEffect(() => { carregar(); }, [carregar, versao]);

  return (
    <Card>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <span>O que a imobiliária tem para este pedido</span>
        <Button size="sm" variant="outline-secondary" onClick={carregar} disabled={carregando}>Atualizar busca</Button>
      </Card.Header>
      <Card.Body>
        {carregando && !dados && <Spinner animation="border" size="sm" />}
        {erro && <Alert variant="warning" className="mb-0">Não consegui buscar os imóveis agora. Tente de novo.</Alert>}
        {dados && (
          <>
            <div className="small text-muted mb-2">Procurando: {dados.criterios.join(' · ') || 'sem critérios'}</div>
            {dados.observacoes.map((o) => <div key={o} className="small text-warning-emphasis mb-1">{o}</div>)}
            {dados.total === 0 ? (
              <Alert variant="secondary" className="mt-2 mb-0">
                Nenhum imóvel da imobiliária atende a este pedido ({dados.catalogo} ativos conferidos). Veja abaixo as ofertas já publicadas nos grupos.
              </Alert>
            ) : (
              <>
                <div className="fw-semibold mt-2 mb-1">
                  {dados.total} imóvel{dados.total > 1 ? 'is' : ''} atende{dados.total > 1 ? 'm' : ''}
                  {dados.total > dados.imoveis.length ? ` (mostrando ${dados.imoveis.length})` : ''}
                </div>
                <ListGroup variant="flush">
                  {dados.imoveis.map((im) => (
                    <ListGroup.Item key={im.publicId} className="px-0">
                      <div className="fw-semibold">{im.titulo}</div>
                      <div className="small text-muted">{im.detalhes}</div>
                      <div className="mt-1 d-flex gap-2 flex-wrap">
                        <Button as={Link} to={`/imoveis/${im.publicId}`} size="sm" variant="outline-primary">Abrir no painel</Button>
                        <Button as="a" href={im.url} target="_blank" rel="noopener noreferrer" size="sm" variant="outline-secondary">Ver no site</Button>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
}

const ROTULO_ENVIO = { pendente: 'na fila da extensão', enviado: 'enviado', erro: 'não foi enviado', expirado: 'expirou sem enviar' };
const COR_ENVIO = { pendente: 'warning', enviado: 'success', erro: 'danger', expirado: 'secondary' };

// Fase 2: ofertas que já foram publicadas nos grupos e servem a este pedido. As mensagens saem pela EXTENSÃO CHROME do corretor (nunca pelo
// desktop), uma por clique dele, com o texto na tela para editar antes.
function OfertasDosGrupos({ publicId, podeEnviar }) {
  const toast = useToast();
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [envios, setEnvios] = useState([]);
  const [perguntando, setPerguntando] = useState(null); // oferta aberta no modal
  const [textoPergunta, setTextoPergunta] = useState('');
  const [pedindo, setPedindo] = useState(false); // modal "pedir no grupo"
  const [grupos, setGrupos] = useState([]);
  const [grupoId, setGrupoId] = useState('');
  const [textoGrupo, setTextoGrupo] = useState('');
  const [enviando, setEnviando] = useState(false);

  const carregarEnvios = useCallback(() => api.get(`/pedidos/${publicId}/envios`).then((r) => setEnvios(r.data)).catch(() => {}), [publicId]);
  useEffect(() => { carregarEnvios(); }, [carregarEnvios]);

  async function buscar() {
    setCarregando(true);
    try {
      setDados((await api.get(`/pedidos/${publicId}/ofertas`)).data);
    } catch (err) {
      toast.erro(err.message || 'Não consegui buscar as ofertas');
    } finally {
      setCarregando(false);
    }
  }

  function abrirPergunta(oferta) {
    setPerguntando(oferta);
    setTextoPergunta(oferta.textoSugerido);
  }

  async function enviarPergunta() {
    setEnviando(true);
    try {
      const r = await api.post(`/pedidos/${publicId}/ofertas/${perguntando.id}/perguntar`, { texto: textoPergunta });
      toast.sucesso('Pergunta enviada para a fila da sua extensão.');
      if (r.data.aviso) toast.info(r.data.aviso);
      setPerguntando(null);
      await Promise.all([buscar(), carregarEnvios()]);
    } catch (err) {
      toast.erro(err.message || 'Não consegui enfileirar a mensagem');
    } finally {
      setEnviando(false);
    }
  }

  async function abrirPedidoNoGrupo() {
    try {
      const [g, t] = await Promise.all([api.get(`/pedidos/${publicId}/grupos`), api.get(`/pedidos/${publicId}/textos`)]);
      setGrupos(g.data);
      setGrupoId(g.data[0]?.id || '');
      setTextoGrupo(t.data.pedidoNoGrupo);
      setPedindo(true);
    } catch (err) {
      toast.erro(err.message || 'Não consegui carregar seus grupos');
    }
  }

  async function enviarPedidoNoGrupo() {
    setEnviando(true);
    try {
      await api.post(`/pedidos/${publicId}/pedir-no-grupo`, { grupoId, texto: textoGrupo });
      toast.sucesso('Pedido enviado para a fila da sua extensão.');
      setPedindo(false);
      await carregarEnvios();
    } catch (err) {
      toast.erro(err.message || 'Não consegui enfileirar a mensagem');
    } finally {
      setEnviando(false);
    }
  }

  const extensaoOk = dados?.extensao?.pronta !== false;

  return (
    <Card className="mt-4">
      <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <span>Ofertas já publicadas nos grupos</span>
        <span className="d-flex gap-2">
          {podeEnviar && <Button size="sm" variant="outline-secondary" onClick={abrirPedidoNoGrupo}>Pedir no grupo</Button>}
          <Button size="sm" variant="outline-primary" onClick={buscar} disabled={carregando}>{dados ? 'Buscar de novo' : 'Buscar nas ofertas dos grupos'}</Button>
        </span>
      </Card.Header>
      <Card.Body>
        {carregando && !dados && <Spinner animation="border" size="sm" />}
        {!dados && !carregando && <div className="text-muted small">Se a imobiliária não tiver o imóvel, veja o que outros corretores já ofereceram nos grupos que você escuta (últimos 45 dias).</div>}
        {dados && !extensaoOk && <Alert variant="warning" className="small">{dados.extensao.motivo} Enquanto isso você pode ver as ofertas, mas não enviar mensagens.</Alert>}
        {dados && extensaoOk && dados.extensao.waConectado === false && <Alert variant="warning" className="small">O WhatsApp Web da sua extensão aparece desconectado: as mensagens saem quando ele conectar.</Alert>}
        {dados && dados.ofertas.length === 0 && <Alert variant="secondary" className="mb-0">Nenhuma oferta dos grupos serve a este pedido ({dados.analisadas} ofertas analisadas). Você pode pedir no grupo.</Alert>}
        {dados && dados.ofertas.length > 0 && (
          <ListGroup variant="flush">
            {dados.ofertas.map((o) => (
              <ListGroup.Item key={o.id} className="px-0">
                <div className="d-flex justify-content-between flex-wrap gap-1">
                  <span className="fw-semibold">{o.resumo || 'Oferta'}</span>
                  <span className="small text-muted">{o.grupo} · {o.diasAtras === 0 ? 'hoje' : `há ${o.diasAtras} dia${o.diasAtras > 1 ? 's' : ''}`}</span>
                </div>
                <div className="small mt-1" style={{ whiteSpace: 'pre-wrap' }}>{o.trecho}</div>
                <div className="mt-1">
                  {o.pendencias.map((pend) => <Badge key={pend} bg="warning" text="dark" className="me-1 fw-normal">{pend}</Badge>)}
                </div>
                <div className="mt-2 d-flex align-items-center flex-wrap gap-2">
                  {o.perguntada ? (
                    <Badge bg={COR_ENVIO[o.perguntada.status] || 'secondary'}>Pergunta {ROTULO_ENVIO[o.perguntada.status] || o.perguntada.status}</Badge>
                  ) : o.podePerguntar ? (
                    <Button size="sm" variant="primary" disabled={!podeEnviar || !extensaoOk} onClick={() => abrirPergunta(o)}>Perguntar ao anunciante</Button>
                  ) : (
                    <span className="small text-muted">Sem telefone de quem anunciou: peça no grupo.</span>
                  )}
                  {o.quem && <span className="small text-muted">{o.quem}{o.telefoneFinal ? ` · final ${o.telefoneFinal}` : ''}</span>}
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}

        {envios.length > 0 && (
          <div className="mt-3 pt-2 border-top small">
            <div className="fw-semibold mb-1">Mensagens deste pedido</div>
            {envios.map((e) => (
              <div key={e.id}>
                <Badge bg={COR_ENVIO[e.status] || 'secondary'} className="me-2">{ROTULO_ENVIO[e.status] || e.status}</Badge>
                {e.tipo === 'grupo' ? `Pedido no grupo ${e.destino}` : `Pergunta ao anunciante (${e.destino})`} · {dataHora(e.criadoEm)}
              </div>
            ))}
          </div>
        )}
      </Card.Body>

      <Modal show={Boolean(perguntando)} onHide={() => setPerguntando(null)} centered>
        <Modal.Header closeButton><Modal.Title className="h6">Perguntar ao anunciante</Modal.Title></Modal.Header>
        <Modal.Body>
          <p className="small text-muted">A mensagem sai pelo WhatsApp da <b>sua extensão Chrome</b>, direto para quem anunciou. Confira e edite o texto.</p>
          <Form.Control as="textarea" rows={5} value={textoPergunta} onChange={(e) => setTextoPergunta(e.target.value)} aria-label="Mensagem ao anunciante" />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setPerguntando(null)}>Cancelar</Button>
          <Button onClick={enviarPergunta} disabled={enviando || textoPergunta.trim().length < 10}>{enviando ? 'Enviando...' : 'Enviar pela minha extensão'}</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={pedindo} onHide={() => setPedindo(false)} centered>
        <Modal.Header closeButton><Modal.Title className="h6">Pedir no grupo</Modal.Title></Modal.Header>
        <Modal.Body>
          {grupos.length === 0 ? (
            <Alert variant="warning" className="mb-0">Não encontrei grupos na sua conta do WhatsLight. Abra o WhatsApp Web com a extensão e marque os grupos.</Alert>
          ) : (
            <>
              <Form.Label htmlFor="pedir-grupo">Grupo</Form.Label>
              <Form.Select id="pedir-grupo" value={grupoId} onChange={(e) => setGrupoId(e.target.value)}>
                {grupos.map((g) => <option key={g.id} value={g.id}>{g.nome}</option>)}
              </Form.Select>
              <Form.Label htmlFor="pedir-texto" className="mt-3">Mensagem (sem nome nem dados do cliente)</Form.Label>
              <Form.Control id="pedir-texto" as="textarea" rows={4} value={textoGrupo} onChange={(e) => setTextoGrupo(e.target.value)} />
              <div className="small text-muted mt-2">Sai pelo WhatsApp da sua extensão Chrome, em um grupo só, por clique seu. Limite de 3 pedidos em grupos por dia.</div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setPedindo(false)}>Cancelar</Button>
          <Button onClick={enviarPedidoNoGrupo} disabled={enviando || !grupoId || textoGrupo.trim().length < 10}>{enviando ? 'Enviando...' : 'Enviar pela minha extensão'}</Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
}

export default function PaginaPedido() {
  const { publicId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { usuario } = useAuth();
  const ehNovo = !publicId;
  const podeEscrever = usuario.permissoes?.pedidos_criar === true;

  const [valores, setValores] = useState(VAZIO);
  const [pedido, setPedido] = useState(null);
  const [carregando, setCarregando] = useState(!ehNovo);
  const [erro, setErro] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [versao, setVersao] = useState(0);
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState('');
  const [existentes, setExistentes] = useState([]);
  const [criarNovo, setCriarNovo] = useState(false);
  const [textoIA, setTextoIA] = useState('');
  const [lendo, setLendo] = useState(false);

  const campo = (nome, valor) => setValores((v) => ({ ...v, [nome]: valor }));
  const desabilitado = !podeEscrever;

  useEffect(() => {
    if (usuario.nivel === 1) api.get('/empresas').then((r) => setEmpresas(r.data)).catch(() => {});
  }, [usuario.nivel]);

  useEffect(() => {
    if (ehNovo) return;
    api
      .get(`/pedidos/${publicId}`)
      .then((r) => {
        setPedido(r.data);
        setValores(deApi(r.data));
        setTextoIA(r.data.textoOriginal || '');
      })
      .catch((err) => {
        setErro(true);
        toast.erro(err.message || 'Não foi possível carregar o pedido');
      })
      .finally(() => setCarregando(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicId, ehNovo]);

  const parametrosEmpresa = usuario.nivel === 1 && empresaId ? { empresa_id: empresaId } : {};

  // Ao sair do telefone/e-mail de um pedido novo: esse cliente já tem cadastro? Se sim, o pedido será vinculado a ele.
  async function conferirCliente() {
    if (!ehNovo || (!valores.telefone && !valores.email)) return;
    try {
      const query = new URLSearchParams({ telefone: valores.telefone, email: valores.email, ...parametrosEmpresa });
      const r = await api.get(`/pedidos/cliente-existente?${query}`);
      setExistentes(r.data);
    } catch {
      setExistentes([]); // best-effort: o servidor confere de novo ao salvar
    }
  }

  async function lerComIA() {
    setLendo(true);
    try {
      const r = await api.post('/pedidos/interpretar', { texto: textoIA, ...parametrosEmpresa });
      const lido = deApi(r.data);
      // a IA só preenche: o que o corretor já digitou no contato não é apagado se ela não achou nada
      setValores((v) => ({ ...lido, nome: lido.nome || v.nome, telefone: lido.telefone || v.telefone, email: lido.email || v.email, whatsapp: v.whatsapp, status: v.status }));
      toast.sucesso('Preenchi o pedido. Confira os campos antes de salvar.');
    } catch (err) {
      toast.erro(err.message || 'Não consegui ler a mensagem');
    } finally {
      setLendo(false);
    }
  }

  async function salvar(e) {
    e.preventDefault();
    setSalvando(true);
    try {
      const corpo = { ...paraApi(valores), textoOriginal: textoIA || valores.textoOriginal, ...parametrosEmpresa };
      if (ehNovo) {
        if (usuario.nivel === 1 && !empresaId) throw new Error('Escolha a empresa.');
        const r = await api.post('/pedidos', { ...corpo, criar_novo_cliente: criarNovo });
        toast.sucesso(r.cliente.vinculo === 'novo' ? `Pedido salvo. Cliente novo cadastrado: ${r.cliente.nome}.` : `Pedido salvo e vinculado ao cliente já cadastrado: ${r.cliente.nome}.`);
        navigate(`/pedidos/${r.data.public_id}`, { replace: true });
      } else {
        const r = await api.put(`/pedidos/${publicId}`, corpo);
        setPedido(r.data);
        setValores(deApi(r.data));
        setVersao((n) => n + 1);
        toast.sucesso('Pedido atualizado.');
      }
    } catch (err) {
      toast.erro(err.message || 'Não foi possível salvar');
    } finally {
      setSalvando(false);
    }
  }

  function alternarFlexivel(chave) {
    setValores((v) => ({ ...v, flexiveis: v.flexiveis.includes(chave) ? v.flexiveis.filter((k) => k !== chave) : [...v.flexiveis, chave] }));
  }

  if (carregando) return <Spinner animation="border" />;
  if (erro) return null;

  return (
    <>
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
        <h1 className="h4 mb-0">{ehNovo ? 'Novo pedido' : 'Pedido'}</h1>
        <Button as={Link} to="/pedidos" variant="outline-secondary" size="sm">Voltar à lista</Button>
      </div>
      {!podeEscrever && <Alert variant="info">Você pode ver este pedido, mas não tem permissão para cadastrar ou editar pedidos.</Alert>}

      <Row className="g-4">
        <Col lg={7}>
          <Form onSubmit={salvar}>
            {podeEscrever && (
              <Card className="mb-3">
                <Card.Header>Mensagem do cliente (opcional)</Card.Header>
                <Card.Body>
                  <Form.Control as="textarea" rows={3} value={textoIA} onChange={(e) => setTextoIA(e.target.value)} placeholder="Cole aqui o que o cliente pediu (ex.: casa até 300 mil que aceite financiamento no Abílio Pedro) e a IA preenche o pedido para você conferir." aria-label="Mensagem do cliente" />
                  <Button className="mt-2" size="sm" variant="outline-primary" onClick={lerComIA} disabled={lendo || textoIA.trim().length < 8}>
                    {lendo ? 'Lendo...' : 'Preencher com IA'}
                  </Button>
                </Card.Body>
              </Card>
            )}

            <Card className="mb-3">
              <Card.Header>Quem está pedindo</Card.Header>
              <Card.Body>
                <Row className="g-3">
                  {usuario.nivel === 1 && ehNovo && (
                    <Col md={12}>
                      <Form.Label htmlFor="ped-empresa">Empresa</Form.Label>
                      <Form.Select id="ped-empresa" value={empresaId} onChange={(e) => setEmpresaId(e.target.value)}>
                        <option value="">Escolha a empresa</option>
                        {empresas.map((e) => <option key={e.public_id} value={e.public_id}>{e.e1_nome}</option>)}
                      </Form.Select>
                    </Col>
                  )}
                  <Col md={12}>
                    <Form.Label htmlFor="ped-nome">Nome</Form.Label>
                    <Form.Control id="ped-nome" value={valores.nome} onChange={(e) => campo('nome', e.target.value)} disabled={desabilitado} required />
                  </Col>
                  <Col md={6}>
                    <Form.Label htmlFor="ped-tel">Telefone (com DDD)</Form.Label>
                    <Form.Control id="ped-tel" value={valores.telefone} onChange={(e) => campo('telefone', e.target.value)} onBlur={conferirCliente} disabled={desabilitado} placeholder="(19) 99999-8888" />
                  </Col>
                  <Col md={6}>
                    <Form.Label htmlFor="ped-email">E-mail</Form.Label>
                    <Form.Control id="ped-email" type="email" value={valores.email} onChange={(e) => campo('email', e.target.value)} onBlur={conferirCliente} disabled={desabilitado} />
                  </Col>
                  {ehNovo && (
                    <Col md={12}>
                      <Form.Check id="ped-whats" label="Esse número tem WhatsApp" checked={valores.whatsapp} onChange={(e) => campo('whatsapp', e.target.checked)} />
                    </Col>
                  )}
                  {ehNovo && existentes.length > 0 && (
                    <Col md={12}>
                      <Alert variant={criarNovo ? 'warning' : 'info'} className="mb-0">
                        {criarNovo ? (
                          <>Será criado um <b>cliente novo</b>, mesmo já existindo {existentes[0].nome} com esse telefone ou e-mail.</>
                        ) : (
                          <>Já existe cadastro: <b>{existentes[0].nome}</b>{existentes[0].telefone ? ` (${existentes[0].telefone})` : ''}. O pedido será <b>vinculado a ele</b>.</>
                        )}
                        <Form.Check className="mt-2" id="ped-outra" label="É outra pessoa: cadastrar como cliente novo" checked={criarNovo} onChange={(e) => setCriarNovo(e.target.checked)} />
                      </Alert>
                    </Col>
                  )}
                  {!ehNovo && pedido?.clientePublicId && (
                    <Col md={12} className="small">
                      Cliente {pedido.clienteVinculo === 'novo' ? 'cadastrado com este pedido' : 'já cadastrado'}: <Link to={`/clientes/${pedido.clientePublicId}`}>ver cadastro</Link>
                    </Col>
                  )}
                </Row>
              </Card.Body>
            </Card>

            <Card className="mb-3">
              <Card.Header>O que procura</Card.Header>
              <Card.Body>
                <Row className="g-3">
                  <Col md={4}>
                    <Form.Label htmlFor="ped-fin">Finalidade</Form.Label>
                    <Form.Select id="ped-fin" value={valores.finalidade} onChange={(e) => campo('finalidade', e.target.value)} disabled={desabilitado}>
                      <option value="venda">Compra</option>
                      <option value="locacao">Locação</option>
                    </Form.Select>
                  </Col>
                  <Col md={4}>
                    <Form.Label htmlFor="ped-tipo">Tipo de imóvel</Form.Label>
                    <Form.Select id="ped-tipo" value={valores.tipoImovel} onChange={(e) => campo('tipoImovel', e.target.value)} disabled={desabilitado}>
                      <option value="">Qualquer</option>
                      {TIPOS_IMOVEL.map(([k, r]) => <option key={k} value={k}>{r}</option>)}
                    </Form.Select>
                  </Col>
                  <Col md={4}>
                    <Form.Label htmlFor="ped-pag">Pagamento</Form.Label>
                    <Form.Select id="ped-pag" value={valores.pagamento} onChange={(e) => campo('pagamento', e.target.value)} disabled={desabilitado}>
                      {PAGAMENTOS.map(([k, r]) => <option key={k} value={k}>{r}</option>)}
                    </Form.Select>
                  </Col>
                  <Col md={4}>
                    <Form.Label htmlFor="ped-vmin">Valor de (R$)</Form.Label>
                    <Form.Control id="ped-vmin" type="number" min={0} step={1000} value={valores.valorMin} onChange={(e) => campo('valorMin', e.target.value)} disabled={desabilitado} placeholder="opcional" />
                  </Col>
                  <Col md={4}>
                    <Form.Label htmlFor="ped-vmax">Valor até (R$)</Form.Label>
                    <Form.Control id="ped-vmax" type="number" min={0} step={1000} value={valores.valorMax} onChange={(e) => campo('valorMax', e.target.value)} disabled={desabilitado} placeholder="ex.: 300000" />
                  </Col>
                  <Col md={4} className="d-flex align-items-end">
                    <Form.Check id="ped-aprox" label="Valor aproximado" checked={valores.valorAproximado} onChange={(e) => campo('valorAproximado', e.target.checked)} disabled={desabilitado} />
                  </Col>
                  {valores.valorMax !== '' && Number(valores.valorMax) > 0 && (
                    <Col md={12} className="small text-muted">
                      {valores.valorMin !== '' && Number(valores.valorMin) > 0 ? `Entre ${dinheiro(valores.valorMin)} e ${dinheiro(valores.valorMax)}` : `Até ${dinheiro(valores.valorMax)} (serve qualquer valor menor)`}
                    </Col>
                  )}
                  {[['dormitorios', 'Dormitórios (mínimo)'], ['suites', 'Suítes (mínimo)'], ['garagens', 'Vagas (mínimo)'], ['banheiros', 'Banheiros (mínimo)']].map(([k, r]) => (
                    <Col md={3} xs={6} key={k}>
                      <Form.Label htmlFor={`ped-${k}`}>{r}</Form.Label>
                      <Form.Control id={`ped-${k}`} type="number" min={0} max={50} value={valores[k]} onChange={(e) => campo(k, e.target.value)} disabled={desabilitado} />
                    </Col>
                  ))}
                  <Col md={12}>
                    <Form.Label htmlFor="ped-bairros">Bairros (separe por vírgula)</Form.Label>
                    <Form.Control id="ped-bairros" value={valores.bairros} onChange={(e) => campo('bairros', e.target.value)} disabled={desabilitado} placeholder="Nossa Senhora das Dores, Abílio Pedro" />
                  </Col>
                  <Col md={6}>
                    <Form.Label htmlFor="ped-cidades">Cidades</Form.Label>
                    <Form.Control id="ped-cidades" value={valores.cidades} onChange={(e) => campo('cidades', e.target.value)} disabled={desabilitado} placeholder="vazio = qualquer" />
                  </Col>
                  <Col md={6}>
                    <Form.Label htmlFor="ped-cond">Condomínio</Form.Label>
                    <Form.Select id="ped-cond" value={valores.emCondominio} onChange={(e) => campo('emCondominio', e.target.value)} disabled={desabilitado}>
                      <option value="">Tanto faz</option>
                      <option value="true">Dentro de condomínio</option>
                      <option value="false">Fora de condomínio</option>
                    </Form.Select>
                  </Col>
                  <Col md={12}>
                    <Form.Label htmlFor="ped-nomecond">Nome do condomínio/edifício (se pediu um específico)</Form.Label>
                    <Form.Control id="ped-nomecond" value={valores.condominios} onChange={(e) => campo('condominios', e.target.value)} disabled={desabilitado} />
                  </Col>
                  <Col md={12}>
                    <Form.Label className="d-block">Só preferência (não elimina o imóvel que não tiver)</Form.Label>
                    {FLEXIVEIS.map(([k, r]) => (
                      <Form.Check inline key={k} id={`ped-flex-${k}`} label={r} checked={valores.flexiveis.includes(k)} onChange={() => alternarFlexivel(k)} disabled={desabilitado} />
                    ))}
                  </Col>
                  <Col md={12}>
                    <Form.Check id="ped-permuta" label="Cliente oferece permuta" checked={valores.oferecePermuta} onChange={(e) => campo('oferecePermuta', e.target.checked)} disabled={desabilitado} />
                  </Col>
                  <Col md={12}>
                    <Form.Check id="ped-alertar" label="Avisar por e-mail quando chegar oferta nova nos grupos que sirva a este pedido" checked={valores.alertar} onChange={(e) => campo('alertar', e.target.checked)} disabled={desabilitado} />
                  </Col>
                  <Col md={12}>
                    <Form.Label htmlFor="ped-obs">Observações</Form.Label>
                    <Form.Control id="ped-obs" as="textarea" rows={2} value={valores.observacoes} onChange={(e) => campo('observacoes', e.target.value)} disabled={desabilitado} placeholder="ex.: perto de escola, quintal grande" />
                  </Col>
                  {!ehNovo && (
                    <Col md={6}>
                      <Form.Label htmlFor="ped-status">Situação</Form.Label>
                      <InputGroup>
                        <Form.Select id="ped-status" value={valores.status} onChange={(e) => campo('status', e.target.value)} disabled={desabilitado}>
                          {STATUS.map(([k, r]) => <option key={k} value={k}>{r}</option>)}
                        </Form.Select>
                      </InputGroup>
                    </Col>
                  )}
                </Row>
              </Card.Body>
            </Card>

            {podeEscrever && (
              <div className="d-flex align-items-center gap-3 mb-4">
                <Button type="submit" disabled={salvando}>{salvando ? 'Salvando...' : ehNovo ? 'Salvar pedido' : 'Salvar alterações'}</Button>
                {!ehNovo && pedido && <span className="small text-muted">Cadastrado em {dataHora(pedido.criadoEm)}{pedido.corretor ? ` por ${pedido.corretor}` : ''}</span>}
              </div>
            )}
          </Form>
        </Col>

        <Col lg={5}>
          {ehNovo ? (
            <Card>
              <Card.Body className="text-muted">
                Depois de salvar, mostro aqui os imóveis da imobiliária que atendem a este pedido.
                <div className="mt-2"><Badge bg="light" text="dark">Dica</Badge> Cole a mensagem do cliente acima e a IA preenche o formulário.</div>
              </Card.Body>
            </Card>
          ) : (
            <>
              <ImoveisDoPedido publicId={publicId} versao={versao} />
              <OfertasDosGrupos publicId={publicId} podeEnviar={podeEscrever} />
            </>
          )}
        </Col>
      </Row>
    </>
  );
}
